import React, { useState, useEffect } from 'react';
import './calendar.scss';

const CalendarComponent = ({ 
  value = new Date(),
  onChange,
  minDate,
  maxDate,
  tileClassName,
  tileDisabled,
  formatMonth,
  className = '',
  locale = 'en-US',
  selectRange = false,
  defaultActiveStartDate,
  selectWeek = false,
  showNeighboringMonth = true
}) => {
  // Determine initial month to display
  const getInitialMonth = () => {
    if (defaultActiveStartDate) {
      return new Date(defaultActiveStartDate);
    }
    if (Array.isArray(value)) {
      return new Date(value[1] || value[0]); // Use end date if available, otherwise start date
    }
    return new Date(value); 
  };

  const [currentMonth, setCurrentMonth] = useState(getInitialMonth());
  const [selectedDate, setSelectedDate] = useState(
    Array.isArray(value) 
      ? value.map(d => d instanceof Date ? d : new Date(d))
      : new Date(value)
  );
  const [view, setView] = useState('month'); // 'month' or 'year'
  const [rangeStart, setRangeStart] = useState(null);
  const [hoverDate, setHoverDate] = useState(null);
  const [hoveredWeek, setHoveredWeek] = useState(null);
  const [lastSelectedWeek, setLastSelectedWeek] = useState(null);

  // Track initial load to only sync with value prop once
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Only sync with value prop on initial load or if explicitly needed
    if (!isInitialized) {
      if (Array.isArray(value)) {
        setSelectedDate(value.map(d => d instanceof Date ? d : new Date(d)));
        // Set current month based on defaultActiveStartDate or range end date
        if (defaultActiveStartDate) {
          setCurrentMonth(new Date(defaultActiveStartDate));
        } else if (value[1]) {
          setCurrentMonth(new Date(value[1])); // Show month with end date
        } else {
          setCurrentMonth(new Date(value[0])); // Fallback to start date
        }
        
        // Initialize lastSelectedWeek for shift+click functionality
        if (selectWeek && value[1]) {
          setLastSelectedWeek(new Date(value[1])); // Remember the end date's week
        }
      } else {
        setSelectedDate(value instanceof Date ? value : new Date(value));
        if (defaultActiveStartDate) {
          setCurrentMonth(new Date(defaultActiveStartDate));
        } else {
          setCurrentMonth(new Date(value));
        }
        
        // Initialize lastSelectedWeek for single date
        if (selectWeek) {
          setLastSelectedWeek(new Date(value));
        }
      }
      setIsInitialized(true);
    }
  }, [value, defaultActiveStartDate, selectWeek, isInitialized]);


  const weekdays = [
    { short: 'Sun', full: 'Sunday' },
    { short: 'Mon', full: 'Monday' },
    { short: 'Tue', full: 'Tuesday' },
    { short: 'Wed', full: 'Wednesday' },
    { short: 'Thu', full: 'Thursday' },
    { short: 'Fri', full: 'Friday' },
    { short: 'Sat', full: 'Saturday' }
  ];
  
  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };
  
  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay();
  };

  const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day;
    d.setDate(diff);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  };

  const getWeekEnd = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + 6;
    d.setDate(diff);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  };

  const isSameWeek = (date1, date2) => {
    const week1Start = getWeekStart(date1);
    const week2Start = getWeekStart(date2);
    return week1Start.getTime() === week2Start.getTime();
  };

  const getWeekRange = (startDate, endDate) => {
    const weekStart1 = getWeekStart(startDate);
    const weekStart2 = getWeekStart(endDate);
    
    // Ensure start is before end
    const rangeStart = weekStart1 <= weekStart2 ? weekStart1 : weekStart2;
    const rangeEnd = weekStart1 <= weekStart2 ? getWeekEnd(endDate) : getWeekEnd(startDate);
    
    return [rangeStart, rangeEnd];
  };
  
  const handlePrevMonth = () => {
    setCurrentMonth(prevMonth => {
      const newMonth = new Date(prevMonth);
      newMonth.setMonth(prevMonth.getMonth() - 1);
      return newMonth;
    });
  };
  
  const handleNextMonth = () => {
    setCurrentMonth(prevMonth => {
      const newMonth = new Date(prevMonth);
      newMonth.setMonth(prevMonth.getMonth() + 1);
      return newMonth;
    });
  };

  const handlePrevYear = () => {
    setCurrentMonth(prevMonth => {
      const newMonth = new Date(prevMonth);
      newMonth.setFullYear(prevMonth.getFullYear() - 1);
      return newMonth;
    });
  };
  
  const handleNextYear = () => {
    setCurrentMonth(prevMonth => {
      const newMonth = new Date(prevMonth);
      newMonth.setFullYear(prevMonth.getFullYear() + 1);
      return newMonth;
    });
  };
  
  const handleDateClick = (day, event = null) => {
    const newDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    
    if (selectWeek) {
      // Week selection with shift+click support
      const isShiftClick = event && event.shiftKey;
      
      if (isShiftClick && lastSelectedWeek) {
        // Shift+click: extend selection from last selected week to clicked week
        const [weekStart, weekEnd] = getWeekRange(lastSelectedWeek, newDate);
        const extendedRange = [weekStart, weekEnd];
        
        setSelectedDate(extendedRange);
        setHoveredWeek(null); // Clear hover state after selection
        // Don't update lastSelectedWeek on shift+click - keep the original anchor
        if (onChange) {
          onChange(extendedRange, event);
        }
      } else {
        // Regular click: select single week
        const weekStart = getWeekStart(newDate);
        const weekEnd = getWeekEnd(newDate);
        const weekRange = [weekStart, weekEnd];
        
        setSelectedDate(weekRange);
        setLastSelectedWeek(newDate); // Remember this week for shift+click
        setHoveredWeek(null); // Clear hover state after selection
        if (onChange) {
          onChange(weekRange);
        }
      }
    } else if (selectRange) {
      if (!rangeStart) {
        // Start of range selection
        setRangeStart(newDate);
        setSelectedDate([newDate, newDate]);
      } else {
        // Complete range selection
        const startDate = rangeStart;
        const endDate = newDate;
        
        // Ensure start date is before end date
        const range = startDate <= endDate ? [startDate, endDate] : [endDate, startDate];
        
        setSelectedDate(range);
        setRangeStart(null);
        
        if (onChange) {
          onChange(range);
        }
      }
    } else {
      // Single date selection
      setSelectedDate(newDate);
      if (onChange) {
        onChange(newDate);
      }
    }
  };
  
  const handleMouseOver = (day) => {
    if (selectRange && rangeStart) {
      const hoverDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      setHoverDate(hoverDate);
    }
  };

  const handleNavigationLabelClick = () => {
    setView(view === 'month' ? 'year' : 'month');
  };

  const handleMonthClick = (monthIndex) => {
    const newDate = new Date(currentMonth.getFullYear(), monthIndex, 1);
    setCurrentMonth(newDate);
    setView('month');
  };
  
  const renderDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDayOfMonth = getFirstDayOfMonth(year, month);
    const today = new Date();
    
    const weeks = [];
    let dayCount = 1;
    
    // Create weeks (6 weeks maximum)
    for (let weekIndex = 0; weekIndex < 6; weekIndex++) {
      const weekDays = [];
      let hasCurrentMonthDays = false;
      
      // Create 7 days for each week
      for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
        const cellIndex = weekIndex * 7 + dayIndex;
        const dayOfWeek = dayIndex;
        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6; // Sunday or Saturday
        
        if (cellIndex < firstDayOfMonth) {
          // Previous month days
          const prevMonth = month === 0 ? 11 : month - 1;
          const prevYear = month === 0 ? year - 1 : year;
          const prevMonthDays = getDaysInMonth(prevYear, prevMonth);
          const day = prevMonthDays - (firstDayOfMonth - cellIndex - 1);
          const date = new Date(prevYear, prevMonth, day);
          
          weekDays.push(renderDayButton(date, true, isWeekend, weekIndex));
        } else if (dayCount > daysInMonth) {
          // Next month days
          const nextMonth = month === 11 ? 0 : month + 1;
          const nextYear = month === 11 ? year + 1 : year;
          const day = dayCount - daysInMonth;
          const date = new Date(nextYear, nextMonth, day);
          
          weekDays.push(renderDayButton(date, true, isWeekend, weekIndex));
          dayCount++;
        } else {
          // Current month days
          const day = dayCount;
          const date = new Date(year, month, day);
          hasCurrentMonthDays = true;
          
          weekDays.push(renderDayButton(date, false, isWeekend, weekIndex, day));
          dayCount++;
        }
      }
      
      if (hasCurrentMonthDays || weekIndex < 2) {
        // Week wrapper for hover effects
        const weekKey = `week-${weekIndex}`;
        const isHoveredWeek = hoveredWeek === weekIndex;
        
        weeks.push(
          <div
            key={weekKey}
            className={`calendar-week ${selectWeek ? 'week-selectable' : ''} ${isHoveredWeek ? 'week-hover' : ''}`}
            style={{ display: 'flex', flexWrap: 'wrap' }}
            onMouseEnter={() => selectWeek && setHoveredWeek(weekIndex)}
            onMouseLeave={() => selectWeek && setHoveredWeek(null)}
            onClick={(event) => {
              // Handle week wrapper clicks for shift+click support
              if (selectWeek && hasCurrentMonthDays) {
                // Calculate a representative date for this week (first day of the week)
                const year = currentMonth.getFullYear();
                const month = currentMonth.getMonth();
                const firstDayOfMonth = getFirstDayOfMonth(year, month);
                const weekStartDay = weekIndex * 7 - firstDayOfMonth + 1;
                
                // Find a valid day in the current month for this week
                let representativeDay = Math.max(1, weekStartDay);
                const daysInMonth = getDaysInMonth(year, month);
                representativeDay = Math.min(representativeDay, daysInMonth);
                
                handleDateClick(representativeDay, event);
              }
            }}
          >
            {weekDays}
          </div>
        );
      }
    }
    
    return weeks;
  };

  const renderDayButton = (date, isNeighboringMonth, isWeekend, weekIndex, day = null) => {
    const today = new Date();
    const actualDay = day || date.getDate();
    
    // Check if date is selected or in selected range
    let isSelected = false;
    let isRangeStart = false;
    let isRangeEnd = false;
    let isInRange = false;
    let isInWeek = false;
    
    if (Array.isArray(selectedDate)) {
      // Range/Week selection
      const [start, end] = selectedDate;
      if (selectWeek && start && end) {
        // Check if date is in selected week range (could be multiple weeks)
        const dateTime = date.getTime();
        const startTime = start.getTime();
        const endTime = end.getTime();
        
        isInWeek = dateTime >= startTime && dateTime <= endTime;
        isSelected = isInWeek; // All dates in range should be selected/active
        isInRange = isInWeek;
        
        // For multi-week selection, determine if this is range start/end
        const dateWeekStart = getWeekStart(date);
        const selectionWeekStart = getWeekStart(start);
        const selectionWeekEnd = getWeekStart(end);
        
        isRangeStart = dateWeekStart.getTime() === selectionWeekStart.getTime();
        isRangeEnd = dateWeekStart.getTime() === selectionWeekEnd.getTime();
        
        // Handle single vs multi-week selection
        if (startTime === endTime) {
          // Single week selection
          isRangeStart = isInWeek;
          isRangeEnd = isInWeek;
        }
      } else {
        // Regular range selection
        isRangeStart = start && 
          date.getDate() === start.getDate() && 
          date.getMonth() === start.getMonth() && 
          date.getFullYear() === start.getFullYear();
          
        isRangeEnd = end && 
          date.getDate() === end.getDate() && 
          date.getMonth() === end.getMonth() && 
          date.getFullYear() === end.getFullYear();
          
        isInRange = start && end && date >= start && date <= end;
        isSelected = isRangeStart || isRangeEnd || isInRange;
      }
    } else {
      // Single date selection
      isSelected = selectedDate && 
        date.getDate() === selectedDate.getDate() && 
        date.getMonth() === selectedDate.getMonth() && 
        date.getFullYear() === selectedDate.getFullYear();
    }
    
    const isToday = date.getDate() === today.getDate() && 
      date.getMonth() === today.getMonth() && 
      date.getFullYear() === today.getFullYear();
    
    const isDisabled = tileDisabled ? tileDisabled({ date, view: 'month' }) : false;
    const isBeforeMinDate = minDate && date < new Date(minDate);
    const isAfterMaxDate = maxDate && date > new Date(maxDate);
    const disabled = isDisabled || isBeforeMinDate || isAfterMaxDate;
    
    let customClassName = '';
    if (tileClassName) {
      const classNameValue = tileClassName({ date, view: 'month' });
      customClassName = classNameValue || '';
    }
    
    const classes = [
      'react-calendar__tile',
      'react-calendar__month-view__days__day',
      isNeighboringMonth ? 'react-calendar__month-view__days__day--neighboringMonth' : '',
      isSelected ? 'react-calendar__tile--active' : '',
      isSelected ? 'react-calendar__tile--hasActive' : '',
      isInRange ? 'react-calendar__tile--range' : '',
      isInWeek ? 'react-calendar__tile--range' : '',
      isRangeStart ? 'react-calendar__tile--rangeStart' : '',
      isRangeEnd ? 'react-calendar__tile--rangeEnd' : '',
      (isRangeStart && isRangeEnd) ? 'react-calendar__tile--rangeBothEnds' : '',
      isToday ? 'react-calendar__tile--now' : '',
      isWeekend ? 'react-calendar__month-view__days__day--weekend' : '',
      customClassName
    ].filter(Boolean).join(' ');
    
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];
    const ariaLabel = `${monthNames[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
    
    const style = {
      flex: '0 0 14.2857%',
      overflow: 'hidden'
    };
    
    return (
      <button 
        key={`day-${date.getTime()}`} 
        className={classes}
        onClick={(event) => !disabled && handleDateClick(actualDay, event)}
        onMouseOver={() => handleMouseOver(actualDay)}
        disabled={disabled}
        type="button"
        style={style}
      >
        <abbr aria-label={ariaLabel} style={{ 
          // visibility: isNeighboringMonth && !showNeighboringMonth ? 'hidden' : 'visible',
          color: isNeighboringMonth && !showNeighboringMonth ? 'rgba(178, 178, 178, 1)' : 'inherit'
        }}>{actualDay}</abbr>
      </button>
    );
  };

  const renderMonths = () => {
    const year = currentMonth.getFullYear();
    const currentMonth_idx = currentMonth.getMonth();
    const selectedDateObj = Array.isArray(selectedDate) 
      ? new Date(selectedDate[1] || selectedDate[0]) 
      : new Date(selectedDate);
    const selectedMonth = selectedDateObj.getMonth();
    const selectedYear = selectedDateObj.getFullYear();
    const today = new Date();
    const currentMonthIdx = today.getMonth();
    const currentYear = today.getFullYear();
    
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const fullMonthNames = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];
    
    const months = [];
    
    for (let i = 0; i < 12; i++) {
      const isSelected = selectedYear === year && selectedMonth === i;
      const isCurrentMonth = currentYear === year && currentMonthIdx === i;
      
      const classes = [
        'react-calendar__tile',
        'react-calendar__year-view__months__month',
        isCurrentMonth ? 'react-calendar__tile--now' : '',
        isSelected ? 'react-calendar__tile--hasActive' : '',
        isSelected ? 'react-calendar__tile--range' : '',
        isSelected ? 'react-calendar__tile--rangeStart' : '',
        isSelected ? 'react-calendar__tile--rangeEnd' : '',
        isSelected ? 'react-calendar__tile--rangeBothEnds' : ''
      ].filter(Boolean).join(' ');
      
      const ariaLabel = `${monthNames[i]} ${year}`;
      
      months.push(
        <button
          key={i}
          className={classes}
          type="button"
          style={{ flex: '0 0 33.3333%', overflow: 'hidden' }}
          onClick={() => handleMonthClick(i)}
        >
          <abbr aria-label={ariaLabel}>{monthNames[i]}</abbr>
        </button>
      );
    }
    
    return months;
  };
  
  const formatMonthDisplay = (date) => {
    return date.toLocaleString(locale, { month: 'long', year: 'numeric' });
  };
  
  return (
    <div className={`react-calendar ${className}`}>
      <div className="react-calendar__navigation">
        <button 
          aria-label="" 
          className="react-calendar__navigation__arrow react-calendar__navigation__prev2-button" 
          type="button"
          onClick={handlePrevYear}
        >
          «
        </button>
        <button 
          aria-label="" 
          className="react-calendar__navigation__arrow react-calendar__navigation__prev-button" 
          type="button"
          onClick={handlePrevMonth}
        >
          ‹
        </button>
        <button 
          aria-label="" 
          className="react-calendar__navigation__label" 
          type="button" 
          style={{ flexGrow: 1 }}
          onClick={handleNavigationLabelClick}
          disabled={view === 'year'}
        >
          <span className="react-calendar__navigation__label__labelText react-calendar__navigation__label__labelText--from">
            {view === 'month' ? formatMonthDisplay(currentMonth) : currentMonth.getFullYear()}
          </span>
        </button>
        <button 
          aria-label="" 
          className="react-calendar__navigation__arrow react-calendar__navigation__next-button" 
          type="button"
          onClick={handleNextMonth}
        >
          ›
        </button>
        <button 
          aria-label="" 
          className="react-calendar__navigation__arrow react-calendar__navigation__next2-button" 
          type="button"
          onClick={handleNextYear}
        >
          »
        </button>
      </div>
      
      <div className="react-calendar__viewContainer">
        {view === 'month' ? (
          <div className="react-calendar__month-view">
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <div style={{ flexGrow: 1, width: '100%' }}>
                <div className="react-calendar__month-view__weekdays" style={{ display: 'flex' }}>
                  {weekdays.map(day => (
                    <div 
                      key={day.short} 
                      className="react-calendar__month-view__weekdays__weekday" 
                      style={{ flex: '0 0 14.2857%', overflow: 'hidden' }}
                    >
                      <abbr aria-label={day.full} title={day.full}>{day.short}</abbr>
                    </div>
                  ))}
                </div>
                
                <div className="react-calendar__month-view__days">
                  {renderDays()}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="react-calendar__year-view">
            <div className="react-calendar__year-view__months" style={{ display: 'flex', flexWrap: 'wrap' }}>
              {renderMonths()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CalendarComponent